/**
 * QuizSession (TASK-014 Step B3 / TASK-015 Phase 2) — a single quiz session,
 * in progress or completed. The URL is the session id
 * (`/read/:id/quiz/:sessionId`); the SERVER decides the phase
 * (`quizSession.phase`, see ReaderController#quiz_session) — this page never
 * infers focus-vs-results itself.
 *
 * Rewires the SAME `QuizFocus`/`QuizResults` components
 * (@arianna/reader-ui/quiz) the old QuizOverlay used — no visual redesign
 * beyond the footer buttons and the confirmation dialogs (task doc).
 *
 * TASK-015 Phase 2 — free navigation + autosave + explicit complete:
 *   - `QuizFocus` is ALWAYS mounted (no more `{!busy && <QuizFocus/>}` gate —
 *     that gate was the cause of the old "flash": it unmounted/remounted the
 *     whole runner on every answer).
 *   - There is no more server-authoritative forward cursor (`idx`) — only
 *     `viewIdx`, freely settable in any direction. Answers are freely
 *     revisable by navigating back to any position.
 *   - Persistence is autosave (debounced 800ms, fire-and-forget, position-
 *     keyed — see quizApi.ts's wire-format note) instead of a per-question
 *     `submitAnswer` round trip. Completing is a distinct, explicit action
 *     (`completeQuiz`) that flushes the full answers map and is the only
 *     point a quiz becomes graded/immutable server-side.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { QuizFocus, QuizResults } from '@arianna/reader-ui/quiz'
import type { QuizFocusSession } from '@arianna/reader-ui/quiz'
import type { BundleQuizQuestion } from '@arianna/shared'
import Text from '@arianna/design-system/Text'
import { saveAnswers, completeQuiz } from './quizApi.js'
import type { QuizAnswersByPosition } from './quizApi.js'
import { ConfirmDialog } from './ConfirmDialog.js'
import { quizHubUrl, quizSessionUrl, padronanzaUrl, overviewUrl } from './urls.js'
import CourseLayout from './CourseLayout.js'
import type { PageComponent } from './inertiaPage.js'
import type {
  QuizSessionPageProps,
  QuizFocusQuestionProp,
  QuizReviewQuestionProp,
  QuizScore,
} from './types.js'

function toFocusBundleQuestion(q: QuizFocusQuestionProp): BundleQuizQuestion {
  return {
    id: q.question_id,
    stem: q.stem,
    options: q.options,
    correctOptionIdx: -1,
    chapterRef: null,
  }
}

function toReviewedBundleQuestion(q: QuizReviewQuestionProp): BundleQuizQuestion {
  return {
    id: q.question_id,
    stem: q.stem,
    options: q.options,
    correctOptionIdx: q.correct_option_idx,
    rationale: q.rationale ?? undefined,
    chapterRef: q.chapter_ref,
    // TASK-017 — snake_case (Ruby presenter) -> camelCase (BundleQuizQuestion).
    // Absent/null on legacy bundles -> undefined, same as `rationale` above:
    // KeySourceBadge (reader-ui) renders nothing when keySource is undefined.
    keySource: q.key_source ?? undefined,
    evidenceQuote: q.evidence_quote ?? undefined,
  }
}

/** Converts a question_id-keyed answers map to the position-keyed wire format quizApi expects. */
function toPositionAnswers(
  questions: QuizFocusQuestionProp[],
  answers: Record<string, number | null>,
): QuizAnswersByPosition {
  const out: QuizAnswersByPosition = {}
  questions.forEach((q, i) => {
    out[i] = answers[q.question_id] ?? null
  })
  return out
}

function countUnanswered(
  questions: QuizFocusQuestionProp[],
  answers: Record<string, number | null>,
): number {
  return questions.filter((q) => answers[q.question_id] == null).length
}

const AUTOSAVE_DEBOUNCE_MS = 800

const QuizSession: PageComponent<QuizSessionPageProps> = ({ shell, quizSession }) => {
  const { manualVersionId } = shell

  if (quizSession.phase === 'results') {
    return (
      <QuizSessionResultsView
        manualVersionId={manualVersionId}
        score={quizSession.score}
        questions={quizSession.questions}
      />
    )
  }

  return (
    <QuizSessionFocusView
      manualVersionId={manualVersionId}
      sessionId={quizSession.id}
      currentIdx={quizSession.current_idx}
      questions={quizSession.questions}
    />
  )
}

QuizSession.layout = (page) => <CourseLayout>{page}</CourseLayout>

export default QuizSession

// ---------------------------------------------------------------------------
// Focus — question-by-question runner. All stems/options for the whole quiz
// arrive in props up front (TASK-015); navigation and answering never touch
// the network directly — only the debounced autosave and the explicit
// "Completa quiz" do.
// ---------------------------------------------------------------------------

interface QuizSessionFocusViewProps {
  manualVersionId: number
  sessionId: string
  currentIdx: number
  questions: QuizFocusQuestionProp[]
}

function QuizSessionFocusView({
  manualVersionId,
  sessionId,
  currentIdx,
  questions,
}: QuizSessionFocusViewProps) {
  const total = questions.length

  // `viewIdx` is the ONLY position concept left — free navigation means
  // there is no separate server-authoritative forward cursor to track.
  const [viewIdx, setViewIdx] = useState(currentIdx)

  // Seeded ONLY from ANSWERED positions (selected_option_idx != null) — a
  // question absent from this map means "never visited", which is NOT the
  // same as "saltata" (visited and left blank). Free navigation makes every
  // position reachable, but reachability isn't the same signal as "the user
  // actually looked at this one and moved on without answering" — an absent
  // entry renders as the neutral pill (QuestionPill's 4th, un-annotated
  // state), and only becomes "saltata" (gold) once `markLeftUnanswered`
  // below explicitly writes `null` for it. `countUnanswered`'s `== null`
  // check treats absent and explicit-null identically for the purposes of
  // the completion count/confirmation (both mean "no answer to grade"), so
  // that signal is unaffected by which of the two a given position is in.
  const [liveAnswers, setLiveAnswers] = useState<Record<string, number | null>>(() => {
    const seed: Record<string, number | null> = {}
    for (const q of questions) {
      if (q.selected_option_idx != null) seed[q.question_id] = q.selected_option_idx
    }
    return seed
  })

  const [completing, setCompleting] = useState(false)
  const [completeError, setCompleteError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)

  // Refs mirroring the latest state — read by the debounced autosave timer,
  // which fires well after the triggering render has committed (800ms is
  // plenty of time for these effects to have run), so no staleness risk
  // there. The SYNCHRONOUS double-click "select + advance/complete" path
  // below deliberately does NOT use these refs (see handleDoubleClickOption)
  // — it builds the up-to-date answers map explicitly instead, since state
  // updates are not guaranteed to have flushed into a ref by the very next
  // line of the same event handler.
  const liveAnswersRef = useRef(liveAnswers)
  const viewIdxRef = useRef(viewIdx)
  useEffect(() => {
    liveAnswersRef.current = liveAnswers
  }, [liveAnswers])
  useEffect(() => {
    viewIdxRef.current = viewIdx
  }, [viewIdx])

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dirtyRef = useRef(false)

  const doSaveNow = useCallback(
    (answers: Record<string, number | null>, viewIdxAtSave: number) => {
      dirtyRef.current = false
      void saveAnswers(
        manualVersionId,
        sessionId,
        toPositionAnswers(questions, answers),
        viewIdxAtSave,
      ).then(
        () => setSaveError(null),
        (err) => setSaveError(err instanceof Error ? err.message : String(err)),
      )
    },
    [manualVersionId, sessionId, questions],
  )

  const scheduleSave = useCallback(() => {
    dirtyRef.current = true
    if (saveTimeoutRef.current != null) clearTimeout(saveTimeoutRef.current)
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null
      doSaveNow(liveAnswersRef.current, viewIdxRef.current)
    }, AUTOSAVE_DEBOUNCE_MS)
  }, [doSaveNow])

  const flushIfDirty = useCallback(() => {
    if (!dirtyRef.current) return
    if (saveTimeoutRef.current != null) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = null
    }
    doSaveNow(liveAnswersRef.current, viewIdxRef.current)
  }, [doSaveNow])

  // Flush on tab hide and on unmount (navigating away, incl. the exit
  // button and completeQuiz's own router.visit) — never on mount.
  useEffect(() => {
    function handleVisibility() {
      if (document.visibilityState === 'hidden') flushIfDirty()
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      flushIfDirty()
    }
  }, [flushIfDirty])

  const focusQuestions = useMemo<BundleQuizQuestion[]>(
    () => questions.map(toFocusBundleQuestion),
    [questions],
  )

  const focusSession: QuizFocusSession = useMemo(() => ({ current_idx: viewIdx }), [viewIdx])

  const skippedCount = useMemo(
    () => countUnanswered(questions, liveAnswers),
    [questions, liveAnswers],
  )

  // Marks the question at `prevIdx` as "saltata" (selected_option_idx: null)
  // ONLY if it's still untouched (absent from the map) — a real answer or an
  // already-existing skip mark is left alone. Called at every point the user
  // moves off a question without answering it: `handleNavigate`,
  // `handleAdvance`, and the advance branch of `handleDoubleClickOption`
  // (there the question just received an answer in the SAME batch, so this
  // is a guaranteed no-op — kept anyway so the marking logic lives in one
  // place, not duplicated per call site). No autosave is scheduled purely
  // for this — `toPositionAnswers`'s `?? null` already sends the same value
  // for an absent entry, so the wire payload is identical either way; the
  // mark only matters for THIS render's pill/counter display.
  const markLeftUnanswered = useCallback(
    (prevIdx: number) => {
      const prevQuestion = questions[prevIdx]
      if (prevQuestion == null) return
      setLiveAnswers((prev) => {
        if (prev[prevQuestion.question_id] !== undefined) return prev
        return { ...prev, [prevQuestion.question_id]: null }
      })
    },
    [questions],
  )

  const handleNavigate = useCallback(
    (i: number) => {
      const clamped = Math.max(0, Math.min(total - 1, i))
      if (clamped === viewIdx) return
      markLeftUnanswered(viewIdx)
      setViewIdx(clamped)
    },
    [total, viewIdx, markLeftUnanswered],
  )

  const handleSelectOption = useCallback(
    (questionId: string, optionIdx: number) => {
      setLiveAnswers((prev) => ({ ...prev, [questionId]: optionIdx }))
      scheduleSave()
    },
    [scheduleSave],
  )

  const handleAdvance = useCallback(() => {
    if (viewIdx >= total - 1) return
    markLeftUnanswered(viewIdx)
    setViewIdx(viewIdx + 1)
  }, [viewIdx, total, markLeftUnanswered])

  const doCompleteWith = useCallback(
    async (answers: Record<string, number | null>) => {
      setCompleting(true)
      setCompleteError(null)
      if (saveTimeoutRef.current != null) {
        clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = null
      }
      dirtyRef.current = false
      try {
        await completeQuiz(manualVersionId, sessionId, toPositionAnswers(questions, answers))
        router.visit(quizSessionUrl(manualVersionId, sessionId))
      } catch (err) {
        setCompleteError(err instanceof Error ? err.message : String(err))
        setCompleting(false)
      }
    },
    [manualVersionId, sessionId, questions],
  )

  const handleComplete = useCallback(() => {
    if (countUnanswered(questions, liveAnswers) > 0) {
      setConfirmOpen(true)
      return
    }
    void doCompleteWith(liveAnswers)
  }, [questions, liveAnswers, doCompleteWith])

  const handleConfirmComplete = useCallback(() => {
    setConfirmOpen(false)
    void doCompleteWith(liveAnswers)
  }, [liveAnswers, doCompleteWith])

  const handleCancelComplete = useCallback(() => {
    setConfirmOpen(false)
  }, [])

  // Double click: select + advance, or — on the last question — enter the
  // SAME complete flow (confirmation included) as the button. Built
  // explicitly from `updatedAnswers` rather than reading back `liveAnswers`/
  // the refs, because `setLiveAnswers` above hasn't necessarily flushed by
  // the time this synchronous handler decides whether to open the
  // confirmation dialog or call `doCompleteWith` directly.
  const handleDoubleClickOption = useCallback(
    (questionId: string, optionIdx: number) => {
      const updatedAnswers = { ...liveAnswers, [questionId]: optionIdx }
      setLiveAnswers(updatedAnswers)
      scheduleSave()

      // A complete request is already in flight (e.g. started via the
      // button) — the selection above still applies (self-healing, it'll go
      // out on the next autosave/complete), but don't ALSO fire a second,
      // concurrent completeQuiz from here. Mirrors the button's own
      // anti-double-submit guard (isCompleting), which this path bypasses
      // otherwise since only the primary button is disabled while completing.
      if (completing) return

      if (viewIdx === total - 1) {
        if (countUnanswered(questions, updatedAnswers) > 0) {
          setConfirmOpen(true)
        } else {
          void doCompleteWith(updatedAnswers)
        }
      } else {
        // The question just answered above is what's being "left" here —
        // markLeftUnanswered is a no-op in practice (it's already in the map
        // with a real value by the time its updater runs), kept only so the
        // marking rule has a single implementation.
        markLeftUnanswered(viewIdx)
        setViewIdx((v) => Math.min(v + 1, total - 1))
      }
    },
    [
      liveAnswers,
      viewIdx,
      total,
      questions,
      scheduleSave,
      doCompleteWith,
      completing,
      markLeftUnanswered,
    ],
  )

  const handleExit = useCallback(() => {
    router.visit(quizHubUrl(manualVersionId))
  }, [manualVersionId])

  if (questions.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-3)',
          padding: 'var(--space-6)',
        }}
      >
        <Text variant="body" style={{ color: 'var(--ink-3)' }}>
          Questo quiz non ha domande disponibili.
        </Text>
        <button
          onClick={handleExit}
          style={{
            padding: '10px 18px',
            fontSize: 14,
            fontWeight: 600,
            background: 'var(--ink)',
            color: 'var(--bg)',
            border: 'none',
            borderRadius: 9,
            cursor: 'pointer',
          }}
        >
          Torna alle verifiche
        </button>
      </div>
    )
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {completeError != null && (
        <div
          style={{
            padding: 'var(--space-3) var(--space-5)',
            background: 'var(--red)',
            color: 'var(--surface)',
            fontSize: 13,
          }}
        >
          {completeError}
        </div>
      )}
      {saveError != null && (
        <div
          style={{
            padding: 'var(--space-2) var(--space-5)',
            background: 'var(--surface-2)',
            color: 'var(--ink-3)',
            fontSize: 12,
            borderBottom: '1px solid var(--line)',
          }}
        >
          Salvataggio non riuscito — verrà ritentato al prossimo cambiamento.
        </div>
      )}
      <QuizFocus
        questions={focusQuestions}
        session={focusSession}
        liveAnswers={liveAnswers}
        onSelectOption={handleSelectOption}
        onNavigate={handleNavigate}
        onAdvance={handleAdvance}
        onComplete={handleComplete}
        isCompleting={completing}
        onDoubleClickOption={handleDoubleClickOption}
        onExit={handleExit}
      />
      {confirmOpen && (
        <ConfirmDialog
          titleId="quiz-complete-confirm-title"
          title={`Hai ${skippedCount} domand${skippedCount === 1 ? 'a' : 'e'} senza risposta`}
          message="Le domande saltate contano come sbagliate. Concludere comunque?"
          confirmLabel="Concludi"
          cancelLabel="Annulla"
          onConfirm={handleConfirmComplete}
          onCancel={handleCancelComplete}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Results — fully server-rendered, no client session cache required. Every
// completed session's full per-question review comes straight from
// ReaderController#quiz_session (QuizPresenter.review_questions).
// ---------------------------------------------------------------------------

interface QuizSessionResultsViewProps {
  manualVersionId: number
  score: QuizScore | null
  questions: QuizReviewQuestionProp[]
}

function QuizSessionResultsView({
  manualVersionId,
  score,
  questions: rawQuestions,
}: QuizSessionResultsViewProps) {
  const questions = useMemo<BundleQuizQuestion[]>(
    () => rawQuestions.map(toReviewedBundleQuestion),
    [rawQuestions],
  )
  const answers = useMemo<Record<string, number | null>>(
    () => Object.fromEntries(rawQuestions.map((q) => [q.question_id, q.selected_option_idx])),
    [rawQuestions],
  )

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <QuizResults
        questions={questions}
        answers={answers}
        storedScore={score}
        onExit={() => router.visit(quizHubUrl(manualVersionId))}
        onRetry={() => router.visit(quizHubUrl(manualVersionId))}
        // "Da rivedere" no longer has its own overlay/tab (TASK-014 — it's a
        // block inside Panoramica, see task doc "Decisioni"); this CTA now
        // lands there instead of re-opening a review panel in place.
        onGoReview={() => router.visit(overviewUrl(manualVersionId))}
        onGoMastery={() => router.visit(padronanzaUrl(manualVersionId))}
      />
    </div>
  )
}
